import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import popimg from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../../Common/Images/ImagesForAlert/popup_close_n.png";
import bg from "./Images/top_blue_panel.png"
import Loading from "../../Common/Loading";
import CustomAlert from "../../Common/CustomAlert";

// Import all question type components
import OQns from './OQns';
import WQns from "../MathsDoctor/WQns";
import FQns from './FQns';
import DQns from './DQns';
import BQns from "./BQns";
import RadioQns from './RadioQns';
import NQns from './NQns';
import IQns from './IQns';
import CQns from './CQns';
import MQns from './MQns';
import LQns from './LQns';
import ImageQns from './ImageQns';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const QUESTION_VIEWPORT_OFFSET = 170;

async function apiFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json() as Promise<T>;
}

async function apiFetchWithAuth<T>(url: string, method: string = "GET", body?: any): Promise<T> {
  const token = sessionStorage.getItem("access_token");
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (token) (headers as any)["Authorization"] = `Bearer ${token}`;

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json() as Promise<T>;
}

function getQuestionTypeFromHtml(html: string, defaultType: string): string {
  // Try to detect from data attribute first
  const dataAttrMatch = html.match(/data-detected-qn-type\s*=\s*["']([^"']+)["']/i);
  if (dataAttrMatch) return dataAttrMatch[1];

  // Try to detect from hidden input
  const inputMatch = html.match(/<input[^>]*id=["']?KSD_DETECTED_QN_TYPE["']?[^>]*value=["']?([^"'>\s]+)["']?/i);
  if (inputMatch) return inputMatch[1];

  return defaultType;
}

function stripPostMessageBridge(html: string): string {
  // Remove the postMessage bridge script that serve-question injects
  // This allows the question components to inject their own bridges
  return html.replace(
    /<script[^>]*>[\s\S]*?\/\* ── MathsDoctor postMessage bridge[\s\S]*?<\/script>/i,
    ''
  );
}


interface CountryOption {
  value: string;
  text: string;
}

interface QuestionData {
  qn_file_url: string;
  qn_catg: string;
  lesson_code: string;
  fname: string;
  strand: number;
  substrand: number;
  year: number;
  topic: number;
  qn_no: number;
  country: string;
  curriculum: string;
  strand_label: string;
  substrand_label: string;
  topic_label: string;
  virtual_keypad: string;
  show_filter: boolean;
  show_nav: boolean;
  show_wrong_ans: boolean;
  qn_info?: string;
  warning?: string;
}

interface QuestionStub {
  strand: number;
  substrand: number;
  yr: number;
  topic: number;
  qn_no: number;
  country: string;
  qn_catg: string;
  lesson_code: string;
  fname: string;
  correct_ans?: string;
  wrong_ans?: string;
  opt_order?: string;
  blank_ans?: string;
}

interface PageState {
  loading: boolean;
  overlayVisible: boolean;
  countries: CountryOption[];
  selectedCountry: string;
  selectedYear: string;
  strand: string;
  substrand: string;
  topic: string;
  qnType: string;
  current: QuestionData | null;
  questions: QuestionStub[];
  currentIndex: number;
  total: number;
  iframeReady: boolean;
  checkResult: string | null;
  warningMsg: string;
  showFilter: boolean;
  showNav: boolean;
  showWrongAns: boolean;
  isReportMode: boolean;
  questionHtmlMap: Record<string, string>; // Store all question HTML
  currentQuestionType: string; // Current question type
  correctAudio: string[];
  incorrectAudio: string[];
}

type Action =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "OVERLAY_HIDE" }
  | { type: "SET_COUNTRIES"; payload: CountryOption[] }
  | { type: "SET_FIELD"; field: keyof PageState; value: string }
  | { type: "SET_SEARCH_RESULT"; questions: QuestionStub[]; current: QuestionData; htmlMap: Record<string, string> }
  | { type: "SET_CURRENT"; current: QuestionData; index: number; htmlMap: Record<string, string>; qnType: string }
  | { type: "QUESTION_LOADED" }
  | { type: "CHECK_RESULT"; payload: string | null }
  | { type: "SET_WARNING"; payload: string }
  | { type: "SET_REPORT_MODE"; questions: QuestionStub[]; current: QuestionData; htmlMap: Record<string, string> }
  | { type: "SET_AUDIO"; correctAudio: string[]; incorrectAudio: string[] };

function reducer(state: PageState, action: Action): PageState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "OVERLAY_HIDE":
      return { ...state, overlayVisible: false };
    case "SET_COUNTRIES":
      return { ...state, countries: action.payload };
    case "SET_FIELD":
      return { ...state, [action.field]: action.value } as PageState;
    case "SET_SEARCH_RESULT":
      return {
        ...state,
        questions: action.questions,
        current: action.current,
        currentIndex: 0,
        total: action.questions.length,
        showFilter: action.current.show_filter,
        showNav: action.current.show_nav,
        showWrongAns: action.current.show_wrong_ans,
        questionHtmlMap: action.htmlMap,
        currentQuestionType: getQuestionTypeFromHtml(action.htmlMap[action.current.fname] || "", action.current.qn_catg),
        loading: false,
        checkResult: null,
      };
    case "SET_CURRENT":
      return {
        ...state,
        current: action.current,
        currentIndex: action.index,
        questionHtmlMap: action.htmlMap,
        currentQuestionType: action.qnType,
        loading: false,
        checkResult: null,
      };
    case "QUESTION_LOADED":
      return { ...state, loading: false };
    case "CHECK_RESULT":
      return { ...state, checkResult: action.payload };
    case "SET_WARNING":
      return { ...state, warningMsg: action.payload, loading: false };
    case "SET_REPORT_MODE":
      return {
        ...state,
        questions: action.questions,
        current: action.current,
        currentIndex: 0,
        total: action.questions.length,
        showFilter: false,
        showNav: true,
        showWrongAns: true,
        isReportMode: true,
        questionHtmlMap: action.htmlMap,
        currentQuestionType: getQuestionTypeFromHtml(action.htmlMap[action.current.fname] || "", action.current.qn_catg),
        loading: false,
      };
    case "SET_AUDIO":
      return { ...state, correctAudio: action.correctAudio, incorrectAudio: action.incorrectAudio };
    default:
      return state;
  }
}

const initialState: PageState = {
  loading: false,
  overlayVisible: true,
  countries: [],
  selectedCountry: "",
  selectedYear: "ALL",
  strand: "",
  substrand: "",
  topic: "",
  qnType: "ALL",
  current: null,
  questions: [],
  currentIndex: 0,
  total: 0,
  iframeReady: false,
  checkResult: null,
  warningMsg: "",
  showFilter: true,
  showNav: false,
  showWrongAns: false,
  isReportMode: false,
  questionHtmlMap: {},
  currentQuestionType: "",
  correctAudio: [],
  incorrectAudio: [],
};

const EnglishCheckQuestions: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const questionRef = useRef<any>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    message: ""
  });

  document.body.style.backgroundColor = "#fff"

  const handleCheck = () => {
    console.log("handleCheck called");
    console.log("questionRef.current:", questionRef.current);
    if (!questionRef.current) return;

    console.log("yashu", questionRef.current.getAnswer?.());
    const answer = questionRef.current.getAnswer?.();
    console.log("Answer from question component:", answer);

    if (!answer) return;

    const isCorrect = answer.marks === 1;

    setAlertInfo({
      show: true,
      message: `Your answer is ${isCorrect ? "Correct" : "Wrong"}`
    });
  };

  // Load countries and initial data
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);

    const loadCountries = async () => {
      try {
        const data = await apiFetch<{ countries: CountryOption[] }>(
          `${API_BASE_URL}api/countries_VQ/`
        );

        console.log("Countries API Response:", data);

        dispatch({
          type: "SET_COUNTRIES",
          payload: data.countries,
        });

        if (data.countries.length > 0) {
          dispatch({
            type: "SET_FIELD",
            field: "selectedCountry",
            value: data.countries[0].value,
          });
        }
      } catch (err) {
        console.error("Failed to load countries:", err);

        dispatch({
          type: "SET_COUNTRIES",
          payload: [],
        });
      }
    };

    const loadAudioFiles = async () => {
      try {
        const data = await apiFetch<{ correct: string[], incorrect: string[] }>(
          `${API_BASE_URL}api/AudioFiles/`
        );
        dispatch({ type: "SET_AUDIO", correctAudio: data.correct, incorrectAudio: data.incorrect });
      } catch { /* non-fatal */ }
    };

    const init = async () => {
      await Promise.all([loadCountries(), loadAudioFiles()]);

      if (qs.get("strand")) {
        dispatch({ type: "SET_LOADING", payload: true });
        try {
          const p = new URLSearchParams({
            strand: qs.get("strand") ?? "0",
            substrand: qs.get("substrand") ?? "0",
            yr: qs.get("yr") ?? "0",
            topic: qs.get("topic") ?? "0",
            qn_no: qs.get("qn_no") ?? "0",
            country: qs.get("country") ?? "",
            curriculum: qs.get("curriculum") ?? "",
          });
          const data = await apiFetch<QuestionData>(`${API_BASE_URL}api/by-params/?${p}`);

          // Load question HTML directly
          const htmlMap: Record<string, string> = {};
          if (data.qn_file_url) {
            try {
              const htmlRes = await fetch(data.qn_file_url);
              let htmlText = await htmlRes.text();
              htmlText = stripPostMessageBridge(htmlText);
              htmlMap[data.fname] = htmlText;
            } catch (e) {
              console.warn("Failed to load question HTML:", e);
            }
          }

          dispatch({ type: "SET_SEARCH_RESULT", questions: [], current: data, htmlMap });
        } catch (e) {
          dispatch({ type: "SET_WARNING", payload: String(e) });
        }
        return;
      }

      if (qs.get("stu_sch") && qs.get("stu_fam") && qs.get("stu_id")) {
        dispatch({ type: "SET_LOADING", payload: true });
        try {
          const p = new URLSearchParams({
            stu_sch: qs.get("stu_sch") ?? "",
            stu_fam: qs.get("stu_fam") ?? "",
            stu_id: qs.get("stu_id") ?? "",
            report_no: qs.get("report_no") ?? "",
          });
          const data = await apiFetch<{
            questions: QuestionStub[];
            current_question: QuestionData;
            warning?: string;
          }>(`${API_BASE_URL}api/report/?${p}`);

          if (data.warning)
            dispatch({ type: "SET_WARNING", payload: data.warning });
          else {
            // Load all question HTMLs
            const htmlMap: Record<string, string> = {};
            if (data.current_question.qn_file_url) {
              try {
                const htmlRes = await fetch(data.current_question.qn_file_url);
                const htmlText = await htmlRes.text();
                htmlMap[data.current_question.fname] = htmlText;
              } catch (e) {
                console.warn("Failed to load question HTML:", e);
              }
            }
            dispatch({ type: "SET_REPORT_MODE", questions: data.questions, current: data.current_question, htmlMap });
          }
        } catch (e) {
          dispatch({ type: "SET_WARNING", payload: String(e) });
        }
        return;
      }
      setTimeout(() => dispatch({ type: "OVERLAY_HIDE" }), 400);
    };

    init();
  }, []);

  // Hide loading overlay after component loads
  useEffect(() => {
    if (!state.loading && state.current) {
      const t = setTimeout(() => dispatch({ type: "OVERLAY_HIDE" }), 500);
      return () => clearTimeout(t);
    }
  }, [state.loading, state.current]);

  const handleSearch = async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const params = new URLSearchParams({
        country: state.selectedCountry,
        yr: state.selectedYear,
        strand: state.strand,
        substrand: state.substrand,
        topic: state.topic,
        qn_type: state.qnType,
      });
      const data = await apiFetch<{
        questions: QuestionStub[];
        total: number;
        current_question: QuestionData;
        warning?: string;
      }>(`${API_BASE_URL}api/search/EC?${params}`);

      if (data.warning || !data.total) {
        setAlertInfo({
          show: true,
          message: data.warning ?? "No records found"
        });
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }

      // Load question HTMLs for all questions
      const htmlMap: Record<string, string> = {};
      if (data.current_question.qn_file_url) {
        try {
          const htmlRes = await fetch(data.current_question.qn_file_url);
          let htmlText = await htmlRes.text();
          htmlText = stripPostMessageBridge(htmlText);
          htmlMap[data.current_question.fname] = htmlText;
        } catch (e) {
          console.warn("Failed to load question HTML:", e);
        }
      }

      dispatch({ type: "SET_SEARCH_RESULT", questions: data.questions, current: data.current_question, htmlMap });
    } catch (e) {
      dispatch({ type: "SET_WARNING", payload: String(e) });
    }
  };

  const navigateTo = async (index: number) => {
    if (index < 0 || index >= state.questions.length) return;
    dispatch({ type: "SET_LOADING", payload: true });
    const q = state.questions[index];

    try {
      let url: string;
      if (state.isReportMode) {
        const p = new URLSearchParams({
          strand: String(q.strand),
          substrand: String(q.substrand),
          yr: String(q.yr),
          topic: String(q.topic),
          qn_no: String(q.qn_no),
          country: q.country,
          correct_ans: q.correct_ans ?? "",
          wrong_ans: q.wrong_ans ?? "",
          opt_order: q.opt_order ?? "",
          blank_ans: q.blank_ans ?? "",
          qn_info_index: String(q.qn_no),
        });
        url = `${API_BASE_URL}api/report/navigate/EC?${p}`;
      } else {
        const p = new URLSearchParams({
          strand: String(q.strand),
          substrand: String(q.substrand),
          yr: String(q.yr),
          topic: String(q.topic),
          qn_no: String(q.qn_no),
          country: q.country,
        });
        url = `${API_BASE_URL}api/navigate/EC?${p}`;
      }
      const data = await apiFetch<QuestionData>(url);

      // Load question HTML
      const htmlMap = { ...state.questionHtmlMap };
      if (data.qn_file_url) {
        try {
          const htmlRes = await fetch(data.qn_file_url);
          let htmlText = await htmlRes.text();
          htmlText = stripPostMessageBridge(htmlText);
          htmlMap[data.fname] = htmlText;
        } catch (e) {
          console.warn("Failed to load question HTML:", e);
        }
      }

      const qnType = getQuestionTypeFromHtml(htmlMap[data.fname] || "", data.qn_catg);
      dispatch({ type: "SET_CURRENT", current: data, index, htmlMap, qnType });
    } catch (e) {
      setAlertInfo({
        show: true,
        message: String(e)
      });

      dispatch({
        type: "SET_LOADING",
        payload: false
      });
    }
  };

  const handleGoToNextPage = () => {
    navigateTo(state.currentIndex + 1);
  };

  const dismissWarning = () => dispatch({ type: "SET_WARNING", payload: "" });
  const dismissResult = () => dispatch({ type: "CHECK_RESULT", payload: null });

  const { current: qd, currentIndex, total } = state;
  const prevDisabled = currentIndex <= 0;
  const nextDisabled = currentIndex >= total - 1;

  const renderQuestionComponent = () => {
    if (!qd || !state.questionHtmlMap[qd.fname]) return null;

    const questionHtml = state.questionHtmlMap[qd.fname];
    const finalQnType = state.currentQuestionType;
    console.log("currentQuestionType:", finalQnType);
    console.log("questionHtml exists:", !!questionHtml);
    const lessonMetaForQuestion = {
      qns: [{
        ...qd,
        QnNo: String(qd.qn_no),
        QnType: qd.qn_catg,
        NoOfPrc: 0,
        NoOfClues: 0,
        NoOfSol: 0,
        LastQnInd: currentIndex + 1,
      }],
    };

    const commonProps = {
      lessonMeta: lessonMetaForQuestion,
      questionHtml,
      currentPage: currentIndex + 1,
      totalPages: Math.max(total, 1),
      goToNextPage: handleGoToNextPage,
      showAlert: (msg: string) => dispatch({ type: "SET_WARNING", payload: msg }),
      correctAudio: state.correctAudio,
      incorrectAudio: state.incorrectAudio,
      assetsBaseUrl: process.env.REACT_APP_ASSETS_BASE_URL,
      sessionId: null,
      isCompleting: false,
      updateSessionPoints: undefined,
      hwid: null,
      subject: "MathsDoctor",
    };

    switch (finalQnType) {
      case 'O':
        console.log("Rendering OQns"); return <OQns
          ref={questionRef}
          {...commonProps}
        />;
      case 'O_RADIO':
        console.log("Rendering OQns");
        return <OQns
          ref={questionRef}
          {...commonProps}
        />;
      case 'O_IMAGE':
        return (
          <ImageQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'W':
        return (
          <WQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'F':
        return (
          <FQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'D':
        return (
          <DQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'B':
        return (
          <BQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'N':
        return (
          <NQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'I':
        return (
          <IQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'C':
        return (
          <CQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'M':
        return (
          <MQns
            ref={questionRef}
            {...commonProps}
          />
        );
      case 'L':
        return (
          <LQns
            ref={questionRef}
            {...commonProps}
          />
        );
      default:
        return <div style={{ color: 'red' }}>Unknown question type: {finalQnType}</div>;
    }
  };

  return (
    <><div
      style={{
        backgroundImage: `url(${popimg}), url(${popbg})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "left center, right center",
        backgroundSize: "30% 100%, 70% 100%",
        height: "16px",
        color: "white",
        padding: "7px 0px",
        fontWeight: "bold",
        fontSize: "12pt",
        fontFamily: "arial",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span style={{ marginLeft: "10px" }}>
        Viewing EnglishCheck Questions
      </span>
      <img
        src={popclose}
        alt="close"
        height="30px"
        style={{ cursor: "pointer" }}
        onClick={() => navigate("/SysAdmin")} />
    </div><div style={{
      width: "100%", height: `calc(100vh - 30px)`, overflow: "hidden",
      position: "relative", fontFamily: "Arial", fontSize: 11
    }}>

        {state.loading && (
          <div style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <Loading />
          </div>
        )}

        {state.warningMsg && (
          <div style={css.modalOverlay}>
            <div style={css.modal}>
              <p style={{ marginBottom: 0 }}>{state.warningMsg}</p>
              <button style={css.modalBtn} onClick={dismissWarning}>OK</button>
            </div>
          </div>
        )}

        {state.checkResult && (
          <div style={css.modalOverlay}>
            <div style={css.modal}>
              <p style={{ marginBottom: 0, color: "black" }}>
                Your answer is <strong>{state.checkResult}</strong>
              </p>
              <button style={css.modalBtn} onClick={dismissResult}>OK</button>
            </div>
          </div>
        )}

        <div
          ref={headerRef}
          style={{
            backgroundImage: `url(${bg})`,
            backgroundRepeat: "repeat",
            width: "100%",
            boxSizing: "border-box",
            overflowY: "auto",
            maxHeight: QUESTION_VIEWPORT_OFFSET,
          }}
        >

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "12pt", paddingTop: "10px" }}>

            {state.showFilter && (
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                flexWrap: "wrap", color: "white"
              }}>

                <span>CUR:</span>
                <select
                  value={state.selectedCountry}
                  onChange={e => dispatch({ type: "SET_FIELD", field: "selectedCountry", value: e.target.value })}
                  style={css.input}
                >
                  {state.countries.map(c => (
                    <option key={c.value} value={c.value}>{c.text}</option>
                  ))}
                </select>

                <span>Yr:</span>
                <select
                  value={state.selectedYear}
                  onChange={e => dispatch({ type: "SET_FIELD", field: "selectedYear", value: e.target.value })}
                  style={css.input}
                >
                  {["ALL", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>

                <span>Str:</span>
                <input value={state.strand}
                  onChange={e => dispatch({ type: "SET_FIELD", field: "strand", value: e.target.value })}
                  style={{ ...css.input, width: 30, textAlign: "center" }} />

                <span>SS:</span>
                <input value={state.substrand}
                  onChange={e => dispatch({ type: "SET_FIELD", field: "substrand", value: e.target.value })}
                  style={{ ...css.input, width: 30, textAlign: "center" }} />

                <span>Top:</span>
                <input value={state.topic}
                  onChange={e => dispatch({ type: "SET_FIELD", field: "topic", value: e.target.value })}
                  style={{ ...css.input, width: 30, textAlign: "center" }} />

                <span>Typ:</span>
                <select
                  value={state.qnType}
                  onChange={e => dispatch({ type: "SET_FIELD", field: "qnType", value: e.target.value })}
                  style={css.input}
                >
                  <option value="ALL">ALL</option>
                  <option value="F">Fill in gaps</option>
                  <option value="I">Identify words</option>
                  <option value="M">Mix &amp; Match</option>
                  <option value="C">Multi-choice</option>
                  <option value="D">Multi Dropdown</option>
                  <option value="N">NumberWiz</option>
                  <option value="O">Objective</option>
                  <option value="L">Ordered List</option>
                  <option value="B">True/False</option>
                  <option value="W">Word</option>
                </select>

                <button onClick={handleSearch} style={css.btn} disabled={state.loading}>
                  {state.loading ? "…" : "GO"}
                </button>
              </div>
            )}

            {state.showNav && total > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "white" }}>
                  <button onClick={() => navigateTo(currentIndex - 1)} disabled={prevDisabled}
                    style={{ ...css.btn, opacity: prevDisabled ? 0.4 : 1 }}>
                    PREV
                  </button>
                  <span style={{ fontSize: 16, fontWeight: "bold" }}>
                    {currentIndex + 1} of {total}
                  </span>
                  <button onClick={() => navigateTo(currentIndex + 1)} disabled={nextDisabled}
                    style={{ ...css.btn, opacity: nextDisabled ? 0.4 : 1 }}>
                    NEXT
                  </button>
                  <div
                    style={{
                      marginLeft: "auto",
                      paddingRight: "10px"
                    }}
                  >
                    <button
                      onClick={handleCheck}
                      style={css.btn}
                    >
                      CHECK
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {qd && (
            <div style={{ color: "white", fontSize: 16, paddingTop: 8, whiteSpace: "nowrap" }}>
              Yr:&nbsp;<CssLabel>{String(qd.year)}</CssLabel>&nbsp;&nbsp;
              Str:&nbsp;<CssLabel>{qd.strand_label}</CssLabel>&nbsp;&nbsp;
              SS:&nbsp;<CssLabel>{qd.substrand_label}</CssLabel>
            </div>
          )}

          {qd && (
            <div style={{ color: "black", fontSize: 16, paddingTop: 6, whiteSpace: "nowrap" }}>
              Top:&nbsp;<CssLabel>{qd.topic_label}</CssLabel>&nbsp;&nbsp;
              Lsn:&nbsp;<CssLabel>{qd.lesson_code}</CssLabel>&nbsp;&nbsp;
              QnNo:&nbsp;<CssLabel>{String(qd.qn_no)}</CssLabel>
            </div>
          )}
        </div>

        <div style={{ height: `calc(100% - ${QUESTION_VIEWPORT_OFFSET}px)`, overflow: "auto", padding: 0 }}>
          {qd ? (
            <table className="QuestionTableT" style={{ width: "100%", height: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {renderQuestionComponent()}
              </tbody>
            </table>
          ) : (
            !state.loading && (
              <div style={{ padding: 24, color: "#666", fontSize: 14 }}>
                Use the filter above and click <strong>GO</strong> to display questions.
              </div>
            )
          )}
        </div>

      </div>
      {
        alertInfo.show && (
          <CustomAlert
            message={alertInfo.message}
            onClose={() =>
              setAlertInfo({
                show: false,
                message: ""
              })
            }
          />
        )
      }</>
  );
};

const CssLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    backgroundColor: "yellow", fontWeight: "bold", fontSize: "14pt",
    color: "blue", fontFamily: "Calibri", paddingLeft: 5, paddingRight: 5,
  }}>
    {children}
  </span>
);

const css = {
  input: {
    border: "1px solid #003300",
    padding: "1px 3px",
    fontSize: 12,
  } as React.CSSProperties,

  btn: {
    padding: "2px 10px",
    cursor: "pointer",
    fontSize: 12,
  } as React.CSSProperties,

  modalOverlay: {
    position: "fixed" as const,
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } as React.CSSProperties,

  modal: {
    backgroundColor: "white",
    padding: "24px 32px",
    borderRadius: 4,
    minWidth: 240,
    textAlign: "center" as const,
    fontFamily: "Arial",
    fontSize: 14,
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  } as React.CSSProperties,

  modalBtn: {
    marginTop: 16,
    padding: "4px 24px",
    cursor: "pointer",
    fontSize: 13,
  } as React.CSSProperties,
};

export default EnglishCheckQuestions;
export { };
